import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider, Popover } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import PrimaryTextField from "../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../../Inputs/PrimarySelect/PrimarySelect";
import useEtaStatus from "../../../../hooks/useEtaStatus/useEtaStatus";
import { formatDate } from "../../../../utils/dateHelpers";
import { formatTitleCase } from "../../../../utils/formatHelpers";

export default function LoadBoardFilters({
  activeUser,
  isAdminUser,
  tabValue,
  allLoads,
  filters,
  setFilters
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { etaStatuses } = useEtaStatus();
  const [daysBack, setDaysBack] = useState("60");
  const [daysOut, setDaysOut] = useState("30");
  const [origin, setOrigin] = useState("States");
  const [originSelected, setOriginSelected] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);
  const [destination, setDestination] = useState("States");
  const [destinationSelected, setDestinationSelected] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState([]);
  const [modes, setModes] = useState([]);
  const [modesOptions, setModesOptions] = useState([]);
  const [brokerageStatuses, setBrokerageStatuses] = useState([]);
  const [brokerageStatusesOptions, setBrokerageStatusesOptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customersOptions, setCustomersOptions] = useState([]);
  const [customerReps, setCustomerReps] = useState([]);
  const [customerRepsOptions, setCustomerRepsOptions] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [accountManagersOptions, setAccountManagersOptions] = useState([]);
  const [operationsReps, setOperationsReps] = useState([]);
  const [operationsRepsOptions, setOperationsRepsOptions] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [dispatchersOptions, setDispatchersOptions] = useState([]);
  const [etaStatus, setEtaStatus] = useState([]);
  const open = Boolean(anchorEl);
  const pid = open ? "load-board-filters-popover" : undefined;

  const options = ["States", "Regions"];

  const handleReset = () => {
    setDaysBack("60");
    setDaysOut("30");
    setOrigin("States");
    setOriginSelected([]);
    setEquipmentTypes([]);
    setModes([]);
    setCustomers([]);
    setCustomerReps([]);
    setAccountManagers([]);
    setOperationsReps([]);
    setEtaStatus([]);

    if (tabValue === 1) {
      let regionName = activeUser.region?.regionName;
      regionName = regionName === "Great Plains" ? "Plains States" : formatTitleCase(regionName.replace(/\s+/g, ""));

      setDestination(isAdminUser ? "Regions" : "States");
      setDestinationSelected(isAdminUser ? [regionName] : []);
      setBrokerageStatuses(["COV", "COVCOM", "DEL", "DIS", "LOA", "LOAD"]);
      setDispatchers(isAdminUser ? [] : [activeUser.userIdMcld]);
      setFilters([
        {
          fieldName: "daysBack",
          params: "60"
        },
        {
          fieldName: "daysOut",
          params: "30"
        },
        {
          fieldName: "brokerageStatus",
          params: "COV||COVCOM||DEL||DIS||LOA||LOAD"
        },
        {
          fieldName: "etaStatus",
          params: "-1||1||2||3||4"
        },
        {
          fieldName: isAdminUser ? "destinationZoneName" : "distpatcherUserId",
          params: isAdminUser ? regionName : activeUser.userIdMcld
        }
      ]);
    } else {
      setDestination("States");
      setDestinationSelected([]);
      setBrokerageStatuses([]);
      setDispatchers([]);
      setFilters([
        {
          fieldName: "daysBack",
          params: "60"
        },
        {
          fieldName: "daysOut",
          params: "30"
        }
      ]);
    }
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = () => {
    const customFilters = [
      { fieldName: "daysBack", params: daysBack },
      { fieldName: "daysOut", params: daysOut },
    ];

    const filterMapping = [
      { condition: originSelected.length > 0, fieldName: origin === "States" ? "originState" : "originZoneName", params: originSelected.length < originOptions.length ? originSelected.join("||") : "" },
      { condition: destinationSelected.length > 0, fieldName: destination === "States" ? "destinationState" : "destinationZoneName", params: destinationSelected.length < destinationOptions.length ? destinationSelected.join("||") : "" },
      { condition: equipmentTypes.length > 0, fieldName: "equipmentTypeId", params: equipmentTypes.length < equipmentTypesOptions.length ? equipmentTypes.join("||") : "" },
      { condition: modes.length > 0, fieldName: "mode", params: modes.length < modesOptions.length ? modes.join("||") : "" },
      { condition: brokerageStatuses.length > 0, fieldName: "brokerageStatus", params: brokerageStatuses.length < brokerageStatusesOptions.length ? brokerageStatuses.join("||") : "" },
      { condition: customers.length > 0, fieldName: "customerName", params: customers.length < customersOptions.length ? customers.join("||") : "" },
      { condition: customerReps.length > 0, fieldName: "customerRep", params: customerReps.length < customerRepsOptions.length ? customerReps.join("||") : "" },
      { condition: accountManagers.length > 0, fieldName: "accountMgr", params: accountManagers.length < accountManagersOptions.length ? accountManagers.join("||") : "" },
      { condition: operationsReps.length > 0, fieldName: "operationsRep", params: operationsReps.length < operationsRepsOptions.length ? operationsReps.join("||") : "" },
      { condition: dispatchers.length > 0, fieldName: "distpatcherUserId", params: dispatchers.length < dispatchersOptions.length ? dispatchers.join("||") : "" },
      { condition: etaStatus.length > 0, fieldName: "etaStatus", params: etaStatus.length < etaStatuses.length ? etaStatus.join("||") : "" }
    ];

    filterMapping.forEach(({ condition, fieldName, params }) => {
      if (condition) {
        customFilters.push({ fieldName, params });
      }
    });

    setFilters(customFilters);
    handleClose();
  };

  const handleOriginChange = value => {
    setOrigin(value);
    setOriginSelected([]);
    const field = value === "States" ? "originState" : "originZoneName";
    setOriginOptions(allLoads.map(load => load[field]).filter((item, index, array) => array.indexOf(item) === index).sort());
  };

  const handleDestinationChange = value => {
    setDestination(value);
    setDestinationSelected([]);
    const field = value === "States" ? "destinationState" : "destinationZoneName";
    setDestinationOptions(allLoads.map(load => load[field]).filter((item, index, array) => array.indexOf(item) === index).sort());
  };

  useEffect(() => {
    if (allLoads.length > 0) {
      const uniqueSorted = (key) =>
        [...new Set(allLoads.map(load => load[key]).filter(item => item !== null && item !== undefined && item !== ""))].sort();

      setOriginOptions(uniqueSorted(origin === "States" ? "originState" : "originZoneName"));
      setDestinationOptions(uniqueSorted(destination === "States" ? "destinationState" : "destinationZoneName"));
      setEquipmentTypesOptions(uniqueSorted("mode"));
      setModesOptions(uniqueSorted("equipmentTypeId"));
      setBrokerageStatusesOptions(uniqueSorted("brokerageStatus"));
      setCustomersOptions(uniqueSorted("customerName"));
      const customerData = allLoads
        .filter(load => load.customerName && load.customerId)
        .map(load => ({ customerName: load.customerName, customerId: load.customerId }))
        .filter((customer, index, self) =>
          index === self.findIndex((c) => (
            c.customerId === customer.customerId
          )));
      localStorage.setItem("customerData", JSON.stringify(customerData));
      setCustomerRepsOptions(uniqueSorted("customerRep"));
      setAccountManagersOptions(uniqueSorted("accountMgr"));
      setOperationsRepsOptions(uniqueSorted("operationsRep"));
      setDispatchersOptions(uniqueSorted("distpatcherUserId"));
    }
  }, [allLoads, origin, destination]);

  useEffect(() => {
    setOriginSelected([]);
    setDestinationSelected([]);

    if (filters.length > 0) {
      const filterMapping = [
        { fieldName: "daysBack", setter: setDaysBack, defaultValue: "60" },
        { fieldName: "daysOut", setter: setDaysOut, defaultValue: "30" },
        { fieldName: ["originState", "originZoneName"], setter: setOrigin, defaultValue: ["States", "Regions"], defaultAll: originOptions, isOrigin: true },
        { fieldName: ["destinationState", "destinationZoneName"], setter: setDestination, defaultValue: ["States", "Regions"], defaultAll: destinationOptions, isDestination: true },
        { fieldName: "equipmentTypeId", setter: setEquipmentTypes, defaultValue: [], defaultAll: equipmentTypesOptions },
        { fieldName: "mode", setter: setModes, defaultValue: [], defaultAll: modesOptions },
        { fieldName: "brokerageStatus", setter: setBrokerageStatuses, defaultValue: [], defaultAll: brokerageStatusesOptions },
        { fieldName: "customerName", setter: setCustomers, defaultValue: [], defaultAll: customersOptions },
        { fieldName: "customerRep", setter: setCustomerReps, defaultValue: [], defaultAll: customerRepsOptions },
        { fieldName: "accountMgr", setter: setAccountManagers, defaultValue: [], defaultAll: accountManagersOptions },
        { fieldName: "operationsRep", setter: setOperationsReps, defaultValue: [], defaultAll: operationsRepsOptions },
        { fieldName: "distpatcherUserId", setter: setDispatchers, defaultValue: [], defaultAll: dispatchersOptions },
        { fieldName: "etaStatus", setter: setEtaStatus, defaultValue: [], defaultAll: tabValue === 1 ? etaStatuses.map(({ id }) => ({ id: id.toString() })) : etaStatuses.filter(({ id }) => id > 0 && id < 4).map(({ id }) => ({ id: id.toString() })).join("||") }
      ];

      filterMapping.forEach(({ fieldName, setter, defaultValue, defaultAll, isOrigin, isDestination }) => {
        const filter = isOrigin || isDestination
          ? filters.find(filter => filter.fieldName === fieldName[0] || filter.fieldName === fieldName[1])
          : filters.find(filter => filter.fieldName === fieldName);
        if (filter) {
          if (isOrigin) {
            setter(filter.fieldName === "originState" ? "States" : "Regions");
            setOriginSelected(filter.params !== "" ? filter.params.split("||") : defaultAll);
          } else if (isDestination) {
            setDestination(filter.fieldName === "destinationState" ? "States" : "Regions");
            setDestinationSelected(filter.params !== "" ? filter.params.split("||") : defaultAll);
          } else if (tabValue !== 1 && filter.fieldName === "etaStatus") {
            setter(filter.params.split("||").filter(p => p !== "-1" && p !== "4"));
          } else {
            setter(fieldName === "daysBack" || fieldName === "daysOut" ? filter.params : filter.params !== "" ? filter.params.split("||") : defaultAll);
          }
        } else {
          if (isOrigin || isDestination) setter(defaultValue[0]);
          else setter(defaultValue);
        }
      });
    } else {
      handleReset();
    }
  }, [tabValue]);

  return (
    <>
      <SecondaryButton
        label="Filter"
        onClick={e => setAnchorEl(e.currentTarget)}
        startIcon={FilterListIcon}
        size="small"
      />
      <Popover
        id={pid}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        slotProps={{
          paper: {
            sx: { boxShadow: "elevation3" },
          }
        }}
      >
        <Box sx={{ width: "280px", maxHeight: "70vh", padding: "20px", overflow: "auto" }}>
          <Box display="flex">
            <Typography variant="h4" color="black_opacity.100">Filter</Typography>
            <CloseIcon
              onClick={handleClose}
              sx={{ fontSize: "20px", color: "black_opacity.60", cursor: "pointer", marginLeft: "auto" }} />
          </Box>
          <Divider sx={{ color: "black_opacity.30", margin: "20px -20px" }} />
          <Box className="load-board-input-container">
            <PrimaryTextField
              label="Days back"
              enableIncrement
              isInteger
              maxLength="3"
              maxValue="365"
              value={daysBack}
              onChange={setDaysBack}
              onBlur={() => { if (daysBack === "") setDaysBack("60"); }}
              tooltip="Maximum value is 365"
            />
            <Box sx={{ marginLeft: "10px" }}>
              <PrimaryTextField
                label="Today's Date"
                value={formatDate(new Date(), "MM/dd/yyyy")}
                onChange={() => { }}
                width="90px"
                disabled
              />
            </Box>
            <Box sx={{ marginLeft: "10px" }}>
              <PrimaryTextField
                label="Days out"
                enableIncrement
                isInteger
                maxLength="3"
                maxValue="60"
                value={daysOut}
                onChange={setDaysOut}
                onBlur={() => { if (daysOut === "") setDaysOut("30"); }}
                tooltip="Maximum value is 60"
              />
            </Box>
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Origin"
              options={options}
              onChange={handleOriginChange}
              value={origin}
              flexGrow={1}
            />
          </Box>
          <Box className="load-board-input-container">
            <Box margin="-15px 10px 0 10px">
              <Typography variant="h5" color="black_opacity.40">
                L
              </Typography>
            </Box>
            <PrimarySelect
              label={origin}
              options={originOptions}
              onChange={setOriginSelected}
              value={originSelected}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Destination"
              options={options}
              onChange={handleDestinationChange}
              value={destination}
              flexGrow={1}
            />
          </Box>
          <Box className="load-board-input-container">
            <Box margin="-15px 10px 0 10px">
              <Typography variant="h5" color="black_opacity.40">
                L
              </Typography>
            </Box>
            <PrimarySelect
              label={destination}
              options={destinationOptions}
              onChange={setDestinationSelected}
              value={destinationSelected}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Revenue Codes"
              options={equipmentTypesOptions}
              onChange={setEquipmentTypes}
              value={equipmentTypes}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Mode(s)"
              options={modesOptions}
              onChange={setModes}
              value={modes}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Brokerage status(s)"
              options={brokerageStatusesOptions}
              onChange={setBrokerageStatuses}
              value={brokerageStatuses}
              flexGrow={1}
              allOption
              multiple
              disabled={tabValue === 1}
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Customer(s)"
              options={customersOptions}
              onChange={setCustomers}
              value={customers}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Customer rep(s)"
              options={customerRepsOptions}
              onChange={setCustomerReps}
              value={customerReps}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Account manager(s)"
              options={accountManagersOptions}
              onChange={setAccountManagers}
              value={accountManagers}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Operations rep(s)"
              options={operationsRepsOptions}
              onChange={setOperationsReps}
              value={operationsReps}
              flexGrow={1}
              allOption
              multiple
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="Dispatchers(s)"
              options={dispatchersOptions}
              onChange={setDispatchers}
              value={dispatchers}
              flexGrow={1}
              allOption
              multiple
              disabled={!isAdminUser && tabValue === 1}
            />
          </Box>
          <Box className="load-board-input-container">
            <PrimarySelect
              label="ETA Status(es)"
              options={tabValue === 1
                ? etaStatuses.map(({ id, name }) => ({ id: id.toString(), name }))
                : etaStatuses
                  .filter(({ id }) => id > 0 && id < 4)
                  .map(({ id, name }) => ({ id: id.toString(), name }))
              }
              onChange={setEtaStatus}
              value={etaStatus}
              nameKey="name"
              valueKey="id"
              flexGrow={1}
              allOption
              multiple
              disabled={tabValue === 1}
            />
          </Box>
          <Divider sx={{ color: "black_opacity.30", margin: "20px -20px" }} />
          <Box>
            <PrimaryButton
              label="Apply Filters"
              onClick={handleSearch}
              width="100%"
            />
          </Box>
          <Box sx={{ marginTop: "10px" }}>
            <SecondaryButton
              label="Reset All Filters"
              onClick={handleReset}
              width="100%"
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
}

LoadBoardFilters.propTypes = {
  activeUser: PropTypes.object.isRequired,
  isAdminUser: PropTypes.bool,
  tabValue: PropTypes.number,
  allLoads: PropTypes.array.isRequired,
  filters: PropTypes.array.isRequired,
  setFilters: PropTypes.func.isRequired
};